export interface User {
  id: string;
  email: string;
  displayName: string;
  role: string;
}

export interface SetupStatus {
  needsSetup: boolean;
  requiresCode: boolean;
}

export class ApiError extends Error {}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: init?.body ? { "content-type": "application/json" } : undefined,
    // Sin esto la cookie de sesión no viaja y todo devuelve 401.
    credentials: "same-origin",
  });

  const text = await response.text();
  const body = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!response.ok) {
    throw new ApiError(
      typeof body.error === "string" ? body.error : `Error ${response.status}`,
    );
  }
  return body as T;
}

export const api = {
  setupStatus: () => request<SetupStatus>("/api/setup/status"),

  createOwner: (data: {
    email: string;
    displayName: string;
    password: string;
    setupCode?: string;
  }) =>
    request<{ user: User }>("/api/setup/owner", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  login: (email: string, password: string) =>
    request<{ user: User }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),

  /**
   * Aprobar o denegar. Mismo endpoint que la pestaña Hoy: decidir por dos
   * puertas distintas sería decidir distinto.
   */
  aprobar: (id: string, aprobada: boolean, nota?: string) =>
    request<{ ok: boolean }>(`/api/hoy/aprobar/${id}`, {
      method: "POST",
      body: JSON.stringify({ aprobada, ...(nota ? { nota } : {}) }),
    }),

  me: () => request<{ user: User }>("/api/auth/me"),

  conversaciones: (archivadas = false) =>
    request<{ conversations: Conversacion[] }>(
      `/api/conversations${archivadas ? "?archivadas=1" : ""}`,
    ),

  renombrar: (id: string, title: string) =>
    request<{ conversation: { id: string } }>(`/api/conversations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),

  archivar: (id: string, isArchived: boolean) =>
    request<{ conversation: { id: string } }>(`/api/conversations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ isArchived }),
    }),

  borrarConversacion: (id: string) =>
    request<{ ok: boolean; mensajesBorrados: number }>(`/api/conversations/${id}`, {
      method: "DELETE",
    }),

  crearConversacion: () =>
    request<{ conversation: { id: string; title: string } }>("/api/conversations", {
      method: "POST",
    }),

  mensajes: (conversacionId: string) =>
    request<{ messages: Mensaje[] }>(`/api/conversations/${conversacionId}/messages`),

  enviar: enviarMensaje,

  /** Con qué cuenta BilboBot ahora mismo. Para no describirlo a mano. */
  estado: () => request<EstadoDeBilboBot>("/api/estado"),

  vozDisponible: () =>
    request<{ disponible: boolean; motivo: string | null }>("/api/voz/estado"),

  /** Manda la grabación y devuelve lo que ha entendido. */
  transcribir: async (conversacionId: string, audio: Blob) => {
    const r = await fetch(`/api/conversations/${conversacionId}/audio`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": audio.type || "audio/webm" },
      body: audio,
    });
    const cuerpo = (await r.json()) as { texto?: string; error?: string };
    if (!r.ok) throw new ApiError(cuerpo.error ?? `Error ${r.status}`);
    return cuerpo.texto ?? "";
  },

  /** Sube un fichero y devuelve su ficha. Los bytes se quedan en el servidor. */
  subirAdjunto: async (fichero: File): Promise<{ adjunto: Adjunto; aviso?: string }> => {
    const r = await fetch(`/api/adjuntos?nombre=${encodeURIComponent(fichero.name)}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": fichero.type || "application/octet-stream" },
      body: fichero,
    });
    const cuerpo = (await r.json()) as { adjunto?: Adjunto; aviso?: string; error?: string };
    if (!r.ok || !cuerpo.adjunto) throw new ApiError(cuerpo.error ?? `Error ${r.status}`);
    return { adjunto: cuerpo.adjunto, ...(cuerpo.aviso ? { aviso: cuerpo.aviso } : {}) };
  },

  /** Devuelve el audio de un texto, o null si el servidor no puede generarlo. */
  leerEnVoz: async (texto: string, corte?: AbortSignal): Promise<Blob | null> => {
    const r = await fetch("/api/voz", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ texto }),
      // Callar tiene que cortar también lo que se está generando: si no, se
      // sigue pagando audio que ya nadie va a oír.
      ...(corte ? { signal: corte } : {}),
    });
    if (!r.ok) return null;
    return await r.blob();
  },
};

export interface Adjunto {
  id: string;
  nombre: string;
  tipo: string;
  clase: "imagen" | "documento";
  bytes: number;
  caracteresDeTexto: number;
}

export interface EstadoDeBilboBot {
  clientes: number;
  ordenadores: { total: number; conectados: number };
  integraciones: { holded: boolean; trello: boolean; correo: boolean; voz: boolean };
}

export interface Conversacion {
  id: string;
  title: string;
  updatedAt: string;
  mensajes: number;
}

/** Un recuerdo que el director consultó —o acaba de guardar— para responder. */
export interface RecuerdoUsado {
  id: string | null;
  titulo: string;
  como: "consultado" | "guardado";
}

/**
 * Una aprobación pedida durante la conversación, con su estado de AHORA.
 *
 * Se pinta como tarjeta bajo la burbuja del asistente, con los botones dentro
 * del chat: la decisión se toma donde está el contexto, no en otra pestaña.
 */
export interface AprobacionEnChat {
  id: string;
  titulo: string;
  queVaAPasar: string;
  porQue: string;
  riesgo: string;
  explicacionDelRiesgo: string;
  comoSeVuelveAtras: string;
  recomendacion?: string;
  estado: "pending" | "granted" | "denied" | "expired" | string;
  /** La tarjeta que la sustituyó (UN sí por web): ésta ya no se puede aprobar. */
  sustituidaPor?: string;
  /** Lo que lleva dentro, desglosado (Fase 4). */
  trabajos?: TrabajoDeLaAprobacion[];
}

export interface Mensaje {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  createdAt?: string;
  /** Con qué respondió. Vacío si no consultó memoria en ese turno. */
  recuerdos?: RecuerdoUsado[];
  /** Lo que iba adjunto al mensaje. */
  adjuntos?: { id: string; nombre: string; tipo: string; clase: "imagen" | "documento"; bytes: number }[];
  /** Lo que dejó esperando tu sí o tu no en ese turno. */
  aprobaciones?: AprobacionEnChat[];
  /** Con qué respondió y cuánto costó (Fase 4). Sólo en las respuestas. */
  pie?: PieDeRespuesta;
  /** Cómo acabó el turno, aparte del texto. */
  cierre?: CierreDeRespuesta | null;
}

export interface ManejadoresStream {
  onThinking?: () => void;
  /**
   * Qué herramienta acaba de usar.
   *
   * Sirve para contar la espera con algo cierto —«mirando Trello»— en vez de
   * con una barra que se inventa un porcentaje que nadie sabe.
   */
  onHerramienta?: (nombre: string) => void;
  onText: (fragmento: string) => void;
  onDone: (info: {
    title: string;
    costCents: number;
    pie?: PieDeRespuesta;
    cierre?: CierreDeRespuesta | null;
    mensajeId?: string;
  }) => void;
  /** El ESTADO del turno, que ya no viaja en el texto (Fase 4). */
  onCierre?: (cierre: CierreDeRespuesta) => void;
  /**
   * El título mejorado, que llega DESPUÉS del `done`.
   *
   * Va aparte porque componerlo cuesta una llamada al modelo y no debe
   * retrasar la respuesta. Puede no llegar nunca: solo se recalcula de vez en
   * cuando, y si falla la conversación se queda con el nombre que tenía.
   */
  onTitulo?: (titulo: string) => void;
  /** Una aprobación recién pedida: la tarjeta sale en vivo, no al recargar. */
  onAprobacion?: (aprobacion: AprobacionEnChat) => void;
  onError: (mensaje: string) => void;
}

/**
 * Envía un mensaje y consume la respuesta en streaming.
 *
 * No se usa EventSource porque solo admite GET, y esto es un POST con cuerpo.
 * Se lee el cuerpo a mano y se parten los eventos SSE, que es poco código y
 * evita inventarse un segundo endpoint solo para satisfacer a la API del
 * navegador.
 */
async function enviarMensaje(
  conversacionId: string,
  content: string,
  manejadores: ManejadoresStream,
  channel: "text" | "voice" = "text",
  /**
   * Para poder cortar a mitad.
   *
   * Al abortar se cierra la conexión, y el servidor lo nota: deja de llamar al
   * modelo y no ejecuta ninguna herramienta más. Sin esto, «parar» sólo dejaba
   * de mirar — al otro lado seguía todo en marcha.
   */
  señal?: AbortSignal,
  /** Las fichas de lo adjuntado, con su texto ya extraído en el servidor. */
  adjuntos: unknown[] = [],
): Promise<void> {
  const response = await fetch(`/api/conversations/${conversacionId}/messages`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ content, channel, adjuntos }),
    ...(señal ? { signal: señal } : {}),
  });

  if (!response.ok || !response.body) {
    const texto = await response.text();
    let mensaje = `Error ${response.status}`;
    try {
      const cuerpo = JSON.parse(texto) as { error?: string };
      if (cuerpo.error) mensaje = cuerpo.error;
    } catch {
      /* respuesta sin JSON */
    }
    manejadores.onError(mensaje);
    return;
  }

  const lector = response.body.getReader();
  const decodificador = new TextDecoder();
  let pendiente = "";

  while (true) {
    const { done, value } = await lector.read();
    if (done) break;

    pendiente += decodificador.decode(value, { stream: true });

    // Los eventos SSE se separan por una línea en blanco. Puede llegar
    // medio evento en un trozo, así que solo se procesan los completos.
    const bloques = pendiente.split("\n\n");
    pendiente = bloques.pop() ?? "";

    for (const bloque of bloques) {
      const evento = /^event: (.+)$/m.exec(bloque)?.[1];
      const datosCrudos = /^data: (.+)$/m.exec(bloque)?.[1];
      if (!evento || !datosCrudos) continue;

      const datos = JSON.parse(datosCrudos) as {
        text?: string;
        title?: string;
        costCents?: number;
        message?: string;
        name?: string;
        id?: string;
        pie?: PieDeRespuesta;
        cierre?: CierreDeRespuesta | null;
        mensajeId?: string;
      };

      if (evento === "text") manejadores.onText(datos.text ?? "");
      else if (evento === "thinking") manejadores.onThinking?.();
      else if (evento === "tool") manejadores.onHerramienta?.(datos.name ?? "");
      else if (evento === "done")
        manejadores.onDone({
          title: datos.title ?? "",
          costCents: datos.costCents ?? 0,
          ...(datos.pie ? { pie: datos.pie } : {}),
          cierre: datos.cierre ?? null,
          ...(datos.mensajeId ? { mensajeId: datos.mensajeId } : {}),
        });
      else if (evento === "cierre")
        manejadores.onCierre?.(datos as unknown as CierreDeRespuesta);
      else if (evento === "titulo") manejadores.onTitulo?.(datos.title ?? "");
      else if (evento === "aprobacion")
        manejadores.onAprobacion?.(datos as unknown as AprobacionEnChat);
      else if (evento === "error")
        manejadores.onError(datos.message ?? "Error desconocido.");
    }
  }
}

/** Lo que llega por el canal cuando un trabajo termina. */
export interface AvisoDeTrabajo {
  jobId: string;
  estado: string;
  titulo: string;
  resumen: string;
  error: string | null;
  artefactos: { id: string; nombre: string; esImagen: boolean }[];
}

/**
 * Escucha el canal de eventos del servidor. Devuelve cómo dejar de escuchar.
 *
 * Aquí sí vale `EventSource`: es un GET, y reconecta solo cuando el móvil
 * pierde la red o el portátil se duerme. Un evento que no se entiende se
 * ignora: no puede tumbar la pantalla que lo está esperando.
 */
export function escucharEventos(m: {
  onMensaje?: (conversacionId: string, mensaje: Mensaje) => void;
  onTrabajo?: (aviso: AvisoDeTrabajo) => void;
}): () => void {
  if (typeof EventSource === "undefined") return () => undefined;
  const canal = new EventSource("/api/eventos", { withCredentials: true });

  canal.addEventListener("mensaje", (e) => {
    try {
      const d = JSON.parse((e as MessageEvent<string>).data) as {
        conversacionId: string;
        mensaje: Mensaje;
      };
      m.onMensaje?.(d.conversacionId, d.mensaje);
    } catch {
      // Uno roto no rompe los siguientes.
    }
  });

  canal.addEventListener("trabajo", (e) => {
    try {
      m.onTrabajo?.(JSON.parse((e as MessageEvent<string>).data) as AvisoDeTrabajo);
    } catch {
      // Igual.
    }
  });

  return () => canal.close();
}

/** Encarga un trabajo. Responde en el acto con su número: el acuse. */
export async function encargar(t: {
  capability: string;
  input: Record<string, unknown>;
  targetWorkerId?: string | null;
}): Promise<{ jobId: string; yaExistia: boolean }> {
  const r = await fetch("/api/jobs", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ ...t, targetWorkerId: t.targetWorkerId ?? null }),
  });
  if (!r.ok) {
    const cuerpo = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(cuerpo.error ?? `El servidor dijo ${r.status}.`);
  }
  return (await r.json()) as { jobId: string; yaExistia: boolean };
}

/** Un trabajo de dentro de una aprobación, como lo desglosa el servidor. */
export interface TrabajoDeLaAprobacion {
  capacidad: string;
  que: string;
  cambio: { antes: string; despues: string } | null;
  lote: { pagina: string; ahora: string | null; pasaraA: string }[];
}

/** Lo que se pinta bajo una respuesta: con qué, cuánto y en cuánto tiempo. */
export interface PieDeRespuesta {
  modelo: string | null;
  esfuerzo: string | null;
  herramientas: { nombre: string; veces: number }[];
  duracionMs: number | null;
  costeCentimos: number | null;
}

/** Cómo acabó un turno: la pastilla, no dos líneas de código. */
export interface CierreDeRespuesta {
  estado: string;
  verificadoPor: string | null;
  porque: string | null;
}
